import { updatePostThunk } from "@/store/posts/postActions";
import { IntegrationType } from "@/store/user/userConstants";
import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Dropdown, message } from "antd";
import Link from "antd/es/typography/Link";
import { ReviewStatus, UpdatePostBody } from "api/config/chalice-types";
import cx from "classnames";
import useAppContext from "config/AppContext/useAppContext";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { isRTLLanguage } from "fixtures/languages";
import { useCategories } from "hooks/useCategoryCRUD";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import { integrationsGetter } from "store/user/userSlice";
import PostCardWrapper, {
  PostCardCommonProps,
} from "../PostCard/PostCardWrapper";
import useScheduleCustomTime from "../PostCard/ScheduleCustomTime/useScheduleCustomTime";
import SocialsSelect from "../PostCard/SocialsSelect";
import useUpdatePostActions from "../utils/useUpdatePostActions";
import ScheduledPostMediaDisplay from "./ScheduledPostMediaDisplay";
import { usePostMedia } from "./hooks/usePostMedia";
import { usePublishDate } from "./hooks/usePublishDate";
import { generateScheduledPostActionItems } from "./scheduledPostCardUtils";

export type ScheduledPostCardProps = {
  post: Post;
} & AllOrNone<{
  autoScheduledTimes: string[];
  autoScheduledIndex: number;
  moveToTop: (index: number) => void;
  moveToBottom: (index: number) => void;
}>;

const ScheduledPostCard = ({
  post,
  autoScheduledTimes,
  autoScheduledIndex,
  moveToTop,
  moveToBottom,
}: ScheduledPostCardProps) => {
  const dispatch = useAppDispatch();
  const { hasPermission, hasSomePermissions } = useUserPermissions();
  const canEditPosts = hasSomePermissions([
    "caption_write",
    "post_design_write",
  ]);
  const publishTo = post.publish_to;
  const setPublishTo = (publishTo: IntegrationType[] | null) => {
    dispatch(updatePostThunk({ post, body: { publish_to: publishTo } }));
  };

  const {
    isLoading,
    loading: loadingPostAction,
    savePost,
    duplicatePost,
    schedulePost,
    discardPost,
    publishPost,
  } = useUpdatePostActions();
  const { realm } = useAppContext();
  const connectedSocials = useAppSelector(integrationsGetter);
  const { categories, isLoadingCategories } = useCategories();
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);

  const { publishPostDate, publishPostDateFormatted } = usePublishDate(
    post,
    autoScheduledIndex,
    autoScheduledTimes
  );
  const { isCarousel, images, mediaURL } = usePostMedia(post);

  const { customTimeMenuItem, setCustomTimeModalOpen, CustomTimeModal } =
    useScheduleCustomTime({ hideIcon: true });

  const handleAction = (which: string) => {
    switch (which) {
      case "save-post":
        return savePost({
          post,
          updateBody: {
            status: "LIKED",
            should_render: true,
            review_status: "NOT_SET" as ReviewStatus,
          } as UpdatePostBody,
        });
      case "publish-post":
        return publishPost(post);
      case "move-to-top":
        return moveToTop && moveToTop(autoScheduledIndex as number);
      case "move-to-bottom":
        return moveToBottom && moveToBottom(autoScheduledIndex as number);
      case "move-to-queue":
        return schedulePost({ post });
      case "duplicate-post":
        return duplicatePost(post);
      case "discard-post":
        return discardPost(post);
    }
  };

  const handleUpdateCategory = async (categoryId: string | null) => {
    if (!post.id) return;
    setIsUpdatingCategory(true);
    try {
      await savePost({
        post,
        updateBody: {
          category_id: categoryId,
          should_render: false,
          review_status: post.review_status ?? "NOT_SET",
        } as unknown as UpdatePostBody,
        showMessage: false,
      });
    } catch (error) {
      console.error("Failed to update category:", error);
      message.error("Failed to update category.");
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const actionItems = useMemo(
    () =>
      generateScheduledPostActionItems(
        canEditPosts,
        hasPermission,
        connectedSocials.length,
        autoScheduledIndex,
        autoScheduledTimes,
        customTimeMenuItem,
        realm.isMarky
      ),
    [
      canEditPosts,
      hasPermission,
      connectedSocials.length,
      autoScheduledIndex,
      autoScheduledTimes,
      customTimeMenuItem,
      realm.isMarky,
    ]
  );

  const isRTL = useMemo(
    () => isRTLLanguage(post.caption || ""),
    [post.caption]
  );

  return (
    <PostCardWrapper {...{ post, loadingPostAction }}>
      {({ openPostEditor }: PostCardCommonProps) => (
        <div
          className={cx("single-scheduled-post", {
            "single-scheduled-post--disabled": isLoading,
          })}
        >
          <ScheduledPostMediaDisplay
            post={post}
            mediaURL={mediaURL}
            isCarousel={isCarousel}
            images={images}
            canEditPosts={canEditPosts}
            openPostEditor={openPostEditor}
          />
          <div className="single-scheduled-post__details">
            <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
              Posting to
              <SocialsSelect publishToState={[publishTo, setPublishTo]} />
              {post.queue_ordering_number ? (
                <span className="scheduled-date">
                  {publishPostDateFormatted}
                </span>
              ) : (
                <Link onClick={() => setCustomTimeModalOpen(true)}>
                  {publishPostDateFormatted}
                </Link>
              )}
            </div>
            {/* <div className="mt-2">
              <PostCategoryTag
                post={post}
                categories={categories}
                isLoadingCategories={isLoadingCategories}
                isUpdatingCategory={isUpdatingCategory}
                disabled={isLoading}
                onUpdateCategory={handleUpdateCategory}
              />
            </div> */}
            <div
              className={cx("single-scheduled-post__caption", {
                "cursor-pointer": canEditPosts,
                "text-right": isRTL,
              })}
              onClick={() => canEditPosts && openPostEditor()}
            >
              {post.caption?.replace(/^\s*[\r\n]/gm, "")}
            </div>
          </div>
          <Dropdown
            menu={{
              items: actionItems,
              onClick: ({ key }) =>
                key === "edit-post" ? openPostEditor(true) : handleAction(key),
            }}
            disabled={isLoading}
          >
            <Button
              className="ml-auto aspect-square"
              size="small"
              icon={<EllipsisOutlined />}
              loading={isLoading}
            />
          </Dropdown>
          <CustomTimeModal post={post} publishAt={publishPostDate} />
        </div>
      )}
    </PostCardWrapper>
  );
};

export default ScheduledPostCard;
